// client/src/coms/views/chatview_parts/chatTextRender.js
import React from 'react';
import YouTubePreviewCard from './YouTubePreviewCard';
import { renderWithCustomEmoji } from '../../customMedia';

function isSameOrigin(href) {
  try {
    return new URL(href, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

// Media from other sites is only fetched after a click, so a posted link cannot be used as a tracking
// pixel (it would reveal every viewer's IP address and online time to that site).
function ClickToLoadMedia({ href, kind, children }) {
  const [show, setShow] = React.useState(() => isSameOrigin(href));
  if (show) return children;
  let host = '';
  try {
    host = new URL(href).host;
  } catch {}
  return (
    <button
      type="button"
      onClick={() => setShow(true)}
      title={href}
      style={{ display: 'block', marginTop: 4, marginBottom: 4, padding: '8px 10px', borderRadius: 8, border: '1px dashed rgba(var(--vx-fg-rgb, 255,255,255), calc(0.2 * var(--vx-ov, 1)))', background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.8 * var(--vx-ov, 1)))', cursor: 'pointer', fontSize: 12 }}
    >
      Show {kind} from {host || 'another site'}
    </button>
  );
}

function splitTrailingPunct(s) {
  let str = String(s || '');
  let trailing = '';
  while (str.length > 0 && /[)\].,!?;:'"]$/.test(str)) {
    trailing = str[str.length - 1] + trailing;
    str = str.slice(0, -1);
  }
  return { core: str, trailing };
}

function tryParseUrl(urlLike) {
  const raw = String(urlLike || '').trim();
  if (!raw) return null;
  try {
    return new URL(raw);
  } catch {}
  try {
    return new URL(raw.toLowerCase().startsWith('www.') ? `https://${raw}` : raw);
  } catch {}
  return null;
}

function getYouTubeVideoId(urlLike) {
  const u = tryParseUrl(urlLike);
  if (!u) return '';

  const host = String(u.hostname || '').toLowerCase().replace(/^www\./, '');
  const path = String(u.pathname || '');

  if (host === 'youtu.be') {
    const id = path.replace(/^\/+/, '').split('/')[0] || '';
    if (/^[A-Za-z0-9_-]{6,}$/.test(id)) return id;
  }

  if (
    host === 'youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'music.youtube.com' ||
    host === 'youtube-nocookie.com'
  ) {
    const v = u.searchParams.get('v');
    if (v && /^[A-Za-z0-9_-]{6,}$/.test(v)) return v;

    const parts = path.split('/').filter(Boolean);
    if (parts.length >= 2 && (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live')) {
      const id = parts[1];
      if (/^[A-Za-z0-9_-]{6,}$/.test(id)) return id;
    }
  }

  return '';
}

function getDisplayLabelForUrl(urlLike) {
  const u = tryParseUrl(urlLike);
  if (!u) return String(urlLike || '').trim();

  const pathname = String(u.pathname || '');
  const last = pathname.split('/').filter(Boolean).pop() || '';
  if (!last) return String(urlLike || '').trim();

  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function LinkPreviewCard({ href }) {
  const u = tryParseUrl(href);
  if (!u) return null;

  const host = String(u.hostname || '').replace(/^www\./, '');
  const title = getDisplayLabelForUrl(href);
  const displayUrl = `${host}${u.pathname || ''}${u.search || ''}${u.hash || ''}`;

  return (
    <a
      href={href}
      style={{
        display: 'block',
        marginTop: 6,
        marginBottom: 6,
        textDecoration: 'none',
        color: 'inherit',
        maxWidth: '100%',
      }}
    >
      <div
        style={{
          borderRadius: 10,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
          background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
          padding: 10,
          transition: 'background 0.15s ease, border-color 0.15s ease',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            fontSize: 12,
            opacity: 0.62,
            marginBottom: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {host}
        </div>

        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: '#6ea0ff',
            marginBottom: 4,
            lineHeight: 1.25,
            wordBreak: 'break-word',
          }}
        >
          {title || host}
        </div>

        <div
          style={{
            fontSize: 12,
            opacity: 0.68,
            lineHeight: 1.35,
            wordBreak: 'break-all',
          }}
        >
          {displayUrl}
        </div>
      </div>
    </a>
  );
}

function isLikelyEmojiChar(ch) {
  if (!ch) return false;
  const code = ch.codePointAt(0);
  if (!code) return false;

  return (
    (code >= 0x1f300 && code <= 0x1faff) ||
    (code >= 0x2600 && code <= 0x26ff) ||
    (code >= 0x2700 && code <= 0x27bf) ||
    (code >= 0x1f1e6 && code <= 0x1f1ff) ||
    code === 0x00a9 ||
    code === 0x00ae ||
    code === 0x203c ||
    code === 0x2049 ||
    code === 0x2122 ||
    code === 0x2139 ||
    code === 0x3030 ||
    code === 0x303d ||
    code === 0x3297 ||
    code === 0x3299
  );
}

function renderTextChunkWithLargeEmoji(text, keyPrefix) {
  const s = String(text == null ? '' : text);
  if (!s) return null;

  const out = [];
  let buffer = '';
  let i = 0;
  let k = 0;

  function pushBuffer() {
    if (!buffer) return;
    out.push(<React.Fragment key={`${keyPrefix}-t-${k++}`}>{buffer}</React.Fragment>);
    buffer = '';
  }

  while (i < s.length) {
    const cp = s.codePointAt(i);
    const ch = String.fromCodePoint(cp);
    const step = cp > 0xffff ? 2 : 1;

    const isVariation = cp === 0xfe0f;
    const isZwj = cp === 0x200d;
    const isSkinTone = cp >= 0x1f3fb && cp <= 0x1f3ff;
    const isEmoji = isLikelyEmojiChar(ch);

    if (isEmoji) {
      pushBuffer();

      let emojiCluster = ch;
      let j = i + step;

      while (j < s.length) {
        const cp2 = s.codePointAt(j);
        const ch2 = String.fromCodePoint(cp2);
        const step2 = cp2 > 0xffff ? 2 : 1;

        const isJoinPiece =
          cp2 === 0xfe0f ||
          cp2 === 0x200d ||
          (cp2 >= 0x1f3fb && cp2 <= 0x1f3ff) ||
          (cp2 >= 0x1f1e6 && cp2 <= 0x1f1ff) ||
          isLikelyEmojiChar(ch2);

        if (!isJoinPiece) break;

        emojiCluster += ch2;
        j += step2;
      }

      out.push(
        <span
          key={`${keyPrefix}-e-${k++}`}
          style={{
            fontSize: '1.9em',
            lineHeight: 1,
            verticalAlign: '-0.12em',
            display: 'inline-block',
          }}
        >
          {emojiCluster}
        </span>
      );

      i = j;
      continue;
    }

    if (isVariation || isZwj || isSkinTone) {
      buffer += ch;
      i += step;
      continue;
    }

    buffer += ch;
    i += step;
  }

  pushBuffer();
  return out;
}

// Custom server emojis (<:name:id>) are rendered as images; everything else as before.
function renderMentionAwareTextChunk(text, keyPrefix) {
  const s = String(text == null ? '' : text);
  if (!s) return null;
  if (s.indexOf('<') === -1) return renderMentionAwareTextChunkPlain(s, keyPrefix);
  return renderWithCustomEmoji(s, (part, key) => renderMentionAwareTextChunkPlain(part, key), keyPrefix);
}

function renderMentionAwareTextChunkPlain(text, keyPrefix) {
  const s = String(text == null ? '' : text);
  if (!s) return null;

  const out = [];
  const mentionRe = /@\[[^\]\n]+\]|@everyone\b|@[A-Za-z0-9_]+/gi;
  let last = 0;
  let match;
  let k = 0;

  while ((match = mentionRe.exec(s)) !== null) {
    const mention = match[0] || '';
    const start = match.index;

    if (start > last) {
      const before = renderTextChunkWithLargeEmoji(s.slice(last, start), `${keyPrefix}-before-${k++}`);
      if (Array.isArray(before)) out.push(...before);
    }

    const label =
      mention.startsWith('@[') && mention.endsWith(']')
        ? `@${mention.slice(2, -1)}`
        : mention;

    out.push(
      <span
        key={`${keyPrefix}-mention-${k++}`}
        style={{
          display: 'inline-block',
          padding: '1px 6px',
          borderRadius: 6,
          background: 'rgba(var(--vx-link-rgb, 110,160,255), 0.16)',
          color: 'var(--vx-link, #8db4ff)',
          fontWeight: 600,
          lineHeight: 'inherit',
        }}
      >
        {label}
      </span>
    );

    last = start + mention.length;
  }

  if (last < s.length) {
    const tail = renderTextChunkWithLargeEmoji(s.slice(last), `${keyPrefix}-tail-${k++}`);
    if (Array.isArray(tail)) out.push(...tail);
  }

  return out;
}

function renderPlainTextWithEmojiAndBreaks(text, keyPrefix) {
  const s = String(text == null ? '' : text);
  if (!s) return null;

  const lines = s.split('\n');
  const out = [];
  let k = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const chunk = renderMentionAwareTextChunk(line, `${keyPrefix}-line-${i}`);
    if (Array.isArray(chunk)) out.push(...chunk);

    if (i < lines.length - 1) {
      out.push(<br key={`${keyPrefix}-br-${k++}`} />);
    }
  }

  return out;
}

function renderInlineMarkdownText(text, keyPrefix) {
  const s = String(text == null ? '' : text);
  if (!s) return null;

  const out = [];
  let pos = 0;
  let k = 0;

  while (pos < s.length) {
    const open = s.indexOf('~~', pos);

    if (open === -1) {
      const tail = renderPlainTextWithEmojiAndBreaks(s.slice(pos), `${keyPrefix}-tail-${k++}`);
      if (Array.isArray(tail)) out.push(...tail);
      break;
    }

    if (open > pos) {
      const before = renderPlainTextWithEmojiAndBreaks(s.slice(pos, open), `${keyPrefix}-txt-${k++}`);
      if (Array.isArray(before)) out.push(...before);
    }

    const close = s.indexOf('~~', open + 2);

    if (close === -1) {
      const rest = renderPlainTextWithEmojiAndBreaks(s.slice(open), `${keyPrefix}-unclosed-${k++}`);
      if (Array.isArray(rest)) out.push(...rest);
      break;
    }

    const strikeInner = s.slice(open + 2, close);

    out.push(
      <span
        key={`${keyPrefix}-strike-${k++}`}
        style={{ textDecoration: 'line-through' }}
      >
        {renderPlainTextWithEmojiAndBreaks(strikeInner, `${keyPrefix}-strike-inner-${k}`)}
      </span>
    );

    pos = close + 2;
  }

  return out;
}

// embeds: false (sender lacked Embed links) keeps every link a plain link: no players or previews.
export function renderTextWithLinks(text, { embeds = true } = {}) {
  const s = String(text == null ? '' : text);
  if (!s) return null;

  const urlRe = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

  const parts = [];
  let last = 0;
  let match;
  let key = 0;

  while ((match = urlRe.exec(s)) !== null) {
    const start = match.index;
    const raw = match[0];

    if (start > last) {
      const chunk = renderInlineMarkdownText(s.slice(last, start), `t${key++}`);
      if (Array.isArray(chunk)) parts.push(...chunk);
    }

    const { core, trailing } = splitTrailingPunct(raw);
    const href = core.toLowerCase().startsWith('www.') ? `https://${core}` : core;

    const coreForExt = core.split(/[?#]/)[0] || core;
    const isImage = /\.(png|jpe?g|gif|webp|svg)$/i.test(coreForExt);
    const isVideo = /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(coreForExt);
    const isAudio = /\.(mp3|wav|ogg|m4a|aac|flac|opus)$/i.test(coreForExt);
    const ytId = getYouTubeVideoId(href);

    if (!embeds) {
      parts.push(
        <a key={`plain${key++}`} href={href}>
          {core}
        </a>
      );
    } else if (ytId) {
      parts.push(<YouTubePreviewCard key={`yt${key++}`} href={href} videoId={ytId} />);
    } else if (isImage) {
      parts.push(
        <ClickToLoadMedia key={`img${key++}`} href={href} kind="image">
        <a href={href}>
          <img
            src={href}
            alt=""
            loading="lazy"
            style={{
              maxWidth: '100%',
              maxHeight: 320,
              borderRadius: 8,
              display: 'block',
              marginTop: 4,
              marginBottom: 4,
            }}
          />
        </a>
        </ClickToLoadMedia>
      );
    } else if (isVideo) {
      parts.push(
        <ClickToLoadMedia key={`vid${key++}`} href={href} kind="video">
        <div
          style={{
            marginTop: 6,
            marginBottom: 6,
          }}
        >
          <video
            controls
            preload="metadata"
            src={href}
            style={{
              maxWidth: '100%',
              maxHeight: 360,
              borderRadius: 8,
              display: 'block',
              background: 'rgba(0,0,0,0.22)',
            }}
          />
        </div>
        </ClickToLoadMedia>
      );
    } else if (isAudio) {
      parts.push(
        <ClickToLoadMedia key={`aud${key++}`} href={href} kind="audio">
        <div
          style={{
            marginTop: 6,
            marginBottom: 6,
          }}
        >
          <audio
            controls
            preload="metadata"
            src={href}
            style={{
              width: '100%',
              maxWidth: 420,
              display: 'block',
            }}
          />
        </div>
        </ClickToLoadMedia>
      );
    } else {
      parts.push(<LinkPreviewCard key={`link${key++}`} href={href} />);
    }

    if (trailing) {
      const trailingChunk = renderInlineMarkdownText(trailing, `p${key++}`);
      if (Array.isArray(trailingChunk)) parts.push(...trailingChunk);
    }

    last = start + raw.length;
  }

  if (last < s.length) {
    const tail = renderInlineMarkdownText(s.slice(last), `t${key++}`);
    if (Array.isArray(tail)) parts.push(...tail);
  }

  return parts;
}